/**
 * @fileoverview Mock API Handlers - 後台訂單管理
 *
 * 命名規範: 後台 API 使用 snake_case 命名 (trade_no, created_at, is_paid)
 *
 * Mock 的 API Endpoints:
 *
 * 1. GET /admin/orders
 *    功能: 後台訂單列表（支援篩選、搜尋、分頁）
 *
 *    Query Parameters:
 *    - status: 訂單狀態篩選
 *      - 0: 未付款 (is_paid=0 AND is_shipped=0)
 *      - 1: 未出貨 (is_paid=1 AND is_shipped=0)
 *      - 2: 已完成 (is_shipped=1)
 *      - 3-5: 特約會員狀態 (本次不實作)
 *    - page: 頁碼 (預設 1)，每頁 3 筆
 *    - keyword: 搜尋關鍵字，搜尋範圍包含 trade_no, full_name, phone, address
 *    - start: 開始日期 (格式: YYYY-MM-DD)
 *    - end: 結束日期 (格式: YYYY-MM-DD)
 *
 *    Response:
 *    ```json
 *    {
 *      "data": {
 *        "items": [
 *          {
 *            "address": "台北市中正區重慶南路1號",
 *            "country": "台灣",
 *            "created_at": "2025-12-25 10:30:00",
 *            "deleted_at": null,
 *            "email": "wang@example.com",
 *            "first_name": null,
 *            "freight": "0",
 *            "full_name": "王小明",
 *            "get_at": "2025-12-25 10:30:00",
 *            "id": "1",
 *            "invoice_head": null,
 *            "invoice_num": null,
 *            "invoice_type": "個人",
 *            "is_check": 1,
 *            "is_get": 1,
 *            "is_notify": 0,
 *            "is_paid": "1",
 *            "is_shipped": "0",
 *            "last_name": null,
 *            "note": "請妥善包裝",
 *            "notification": "電子郵件通知",
 *            "paid_at": "2025-12-25 14:20:00",
 *            "phone": "0912345678",
 *            "postal_code": "10001",
 *            "postal_trace_code": null,
 *            "postal_cost": null,
 *            "report": [
 *              {
 *                "payment_account_last_five": "12345",
 *                "payment_amount": 15500,
 *                "payment_bank_name": "台灣銀行",
 *                "updated_at": "2024-12-21 14:20:00"
 *              }
 *            ],
 *            "send_way": "郵局宅配",
 *            "shipped_at": null,
 *            "sum": "15500",
 *            "trade_no": "TRD20241220001",
 *            "updated_at": "2025-12-25 14:20:00",
 *            "user_id": 33
 *          }
 *        ],
 *        "pagination": {
 *          "from": 1,
 *          "lastPage": 1,
 *          "to": 3,
 *          "total": 3
 *        }
 *      }
 *    }
 *    ```
 *
 *    說明: 列表不包含 order_items，需透過詳情 API 取得商品明細
 *
 * 2. GET /admin/orders/:id
 *    功能: 後台訂單詳情
 *
 *    Response:
 *    ```json
 *    {
 *      "data": {
 *        "address": "台北市中正區重慶南路1號",
 *        "country": "台灣",
 *        "created_at": "2025-12-25 10:30:00",
 *        "deleted_at": null,
 *        "email": "wang@example.com",
 *        "first_name": null,
 *        "freight": "0",
 *        "full_name": "王小明",
 *        "get_at": "2025-12-25 10:30:00",
 *        "id": "1",
 *        "invoice_head": null,
 *        "invoice_num": null,
 *        "invoice_type": "個人",
 *        "is_check": 1,
 *        "is_get": 1,
 *        "is_notify": 0,
 *        "is_paid": "1",
 *        "is_shipped": "0",
 *        "last_name": null,
 *        "note": "請妥善包裝",
 *        "notification": "電子郵件通知",
 *        "order_items": [
 *          {
 *            "accessory_name": "紅色掛繩",
 *            "accessory_price": 100,
 *            "amount": 2,
 *            "engraving_content": {
 *              "type": "default",
 *              "content": {
 *                "gift": "信眾 王小明",
 *                "godName": "天上聖母",
 *                "respect": "敬祝聖安",
 *                "templeName": "天后宮"
 *              }
 *            },
 *            "gold_fee": 5000,
 *            "gold_price": 2500,
 *            "id": "1",
 *            "image_url": "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNk+P+/HgAFhAJ/wlseKgAAAABJRU5ErkJggg==",
 *            "length": 5,
 *            "shape": "如意",
 *            "style": "經典福氣",
 *            "unit": "cm",
 *            "way": "A",
 *            "weight": "10"
 *          }
 *        ],
 *        "paid_at": "2025-12-25 14:20:00",
 *        "phone": "0912345678",
 *        "postal_code": "10001",
 *        "postal_cost": null,
 *        "postal_trace_code": null,
 *        "report": [
 *          {
 *            "payment_account_last_five": "12345",
 *            "payment_amount": 15500,
 *            "payment_bank_name": "台灣銀行",
 *            "updated_at": "2024-12-21 14:20:00"
 *          }
 *        ],
 *        "send_way": "郵局宅配",
 *        "shipped_at": null,
 *        "sum": "15500",
 *        "trade_no": "TRD20241220001",
 *        "updated_at": "2025-12-25 14:20:00",
 *        "user": {
 *          "account": "bbb",
 *          "address": null,
 *          "country": "台灣",
 *          "created_at": "2019-10-07 20:51:53",
 *          "email": null,
 *          "first_name": null,
 *          "id": 33,
 *          "is_admin": 1,
 *          "last_name": null,
 *          "level": 1,
 *          "phone": null,
 *          "postal_code": null,
 *          "special_member": null,
 *          "updated_at": "2021-03-22 22:35:33"
 *        },
 *        "user_id": 33
 *      }
 *    }
 *    ```
 *
 * 3. GET /admin/orders/:id/items
 *    功能: 訂單商品明細（列印用格式）
 *
 *    Response:
 *    ```json
 *    {
 *      "data": [
 *        {
 *          "address": "台灣10001 台北市中正區重慶南路1號",
 *          "amount": 2,
 *          "engraving_content": {
 *            "type": "default",
 *            "content": {
 *              "gift": "信眾 王小明",
 *              "godName": "天上聖母",
 *              "respect": "敬祝聖安",
 *              "templeName": "天后宮"
 *            }
 *          },
 *          "imageUrl": "data:image/png;base64,...",
 *          "orderId": "TRD20241220001",
 *          "recipient": "王小明",
 *          "size": {
 *            "length": 5,
 *            "unit": "cm",
 *            "way": "A",
 *            "weight": "10"
 *          },
 *          "style": "經典福氣"
 *        }
 *      ]
 *    }
 *    ```
 *
 *    說明: 此格式專為列印訂單貼紙設計，每個商品包含完整的收件人和地址資訊
 */

import { rest } from "msw";
import { mockOrders, findOrderById, findUserById } from "../data/mockOrders";

// 從環境變數獲取 API URL
const API_BASE_URL = process.env.VUE_APP_API_URL;

// 輔助函數：根據 status 篩選訂單
function filterOrdersByStatus(orders, status) {
  const statusNum = parseInt(status);

  switch (statusNum) {
    case "0": // 未付款
      return orders.filter((o) => o.is_paid === 0 && o.is_shipped === 0);
    case "1": // 未出貨
      return orders.filter((o) => o.is_paid === 1 && o.is_shipped === 0);
    case "2": // 已完成
      return orders.filter((o) => o.is_shipped === 1);
    case "3": // 特約未付款（本次不實作）
    case "4": // 特約未出貨（本次不實作）
    case "5": // 特約已完成（本次不實作）
      return [];
    default:
      return orders;
  }
}

// 輔助函數：根據關鍵字篩選訂單
function filterOrdersByKeyword(orders, keyword) {
  if (!keyword) return orders;

  const lowerKeyword = keyword.toLowerCase();
  return orders.filter((order) => {
    const searchableFields = [
      order.trade_no,
      order.full_name,
      order.phone,
      order.postal_code + order.address,
    ]
      .join(" ")
      .toLowerCase();

    return searchableFields.includes(lowerKeyword);
  });
}

// 輔助函數：根據日期範圍篩選訂單
function filterOrdersByDateRange(orders, start, end) {
  if (!start && !end) return orders;

  return orders.filter((order) => {
    const orderDate = new Date(order.created_at);
    if (start && orderDate < new Date(start)) return false;
    if (end && orderDate > new Date(end + " 23:59:59")) return false;
    return true;
  });
}

// 輔助函數：計算分頁資訊
function getPaginationInfo(totalItems, page, itemsPerPage = 3) {
  const pageNum = parseInt(page) || 1;
  const total = totalItems;
  const lastPage = Math.ceil(total / itemsPerPage);
  const startIdx = (pageNum - 1) * itemsPerPage;
  const endIdx = Math.min(startIdx + itemsPerPage, total);

  return {
    from: total > 0 ? startIdx + 1 : 0,
    to: endIdx,
    total,
    lastPage: lastPage || 1,
  };
}

// Handler 1: GET /admin/orders - 後台訂單列表
const getAdminOrdersListHandler = rest.get(
  `${API_BASE_URL}admin/orders`,
  (req, res, ctx) => {
    // 解析查詢參數
    const status = req.url.searchParams.get("status") || "0";
    const page = req.url.searchParams.get("page") || "1";
    const keyword = req.url.searchParams.get("keyword") || "";
    const start = req.url.searchParams.get("start") || "";
    const end = req.url.searchParams.get("end") || "";

    // 依序篩選訂單
    let filteredOrders = [...mockOrders];
    filteredOrders = filterOrdersByStatus(filteredOrders, status);
    filteredOrders = filterOrdersByKeyword(filteredOrders, keyword);
    filteredOrders = filterOrdersByDateRange(filteredOrders, start, end);

    // 計算分頁
    const itemsPerPage = 3;
    const pageNum = parseInt(page) || 1;
    const startIdx = (pageNum - 1) * itemsPerPage;
    const endIdx = startIdx + itemsPerPage;
    const paginatedOrders = filteredOrders.slice(startIdx, endIdx);

    // 計算分頁資訊
    const pagination = getPaginationInfo(
      filteredOrders.length,
      page,
      itemsPerPage
    );

    // 移除每筆訂單的 order_items（列表不需要商品明細）
    const itemsWithoutOrderItems = paginatedOrders.map((order) => {
      const { order_items, ...orderWithoutItems } = order;
      return orderWithoutItems;
    });

    // 返回格式：{ data: { items: [...], pagination: {...} } }
    return res(
      ctx.status(200),
      ctx.json({
        data: {
          items: itemsWithoutOrderItems,
          pagination,
        },
      })
    );
  }
);

// Handler 2: GET /admin/orders/:id - 後台訂單詳情
const getAdminOrderDetailHandler = rest.get(
  `${API_BASE_URL}admin/orders/:id`,
  (req, res, ctx) => {
    const { id } = req.params;

    // 從 mock 資料庫查找訂單
    const order = findOrderById(id);

    if (!order) {
      return res(ctx.status(404), ctx.json({ message: "訂單不存在" }));
    }

    // 根據 user_id 查找用戶資料
    const user = findUserById(order.user_id) || {
      id: order.user_id,
      is_admin: 0,
      level: 0,
      account: "unknown",
      password: "",
      first_name: null,
      last_name: null,
      phone: null,
      email: null,
      country: "台灣",
      postal_code: null,
      address: null,
      created_at: "2019-01-01 00:00:00",
      updated_at: "2019-01-01 00:00:00",
      special_member: null,
    };

    // 返回格式：{ data: { ...訂單詳情, order_items: [...], user: {...} } }
    return res(
      ctx.status(200),
      ctx.json({
        data: {
          ...order,
          user,
        },
      })
    );
  }
);

// Handler 3: GET /admin/orders/:id/items - 訂單商品明細（列印用）
const getAdminOrderItemsHandler = rest.get(
  `${API_BASE_URL}admin/orders/:id/items`,
  (req, res, ctx) => {
    const { id } = req.params;

    // 從 mock 資料庫查找訂單
    const order = findOrderById(id);

    if (!order) {
      return res(ctx.status(404), ctx.json({ message: "訂單不存在" }));
    }

    // 將商品轉換為列印格式
    const printItems = order.order_items.map((item) => ({
      orderId: order.trade_no,
      recipient: order.full_name,
      address: `${order.country}${order.postal_code} ${order.address}`,
      imageUrl: item.image_url,
      size: {
        way: item.way,
        length: item.length,
        unit: item.unit,
        weight: item.weight,
      },
      style: item.style,
      amount: item.amount,
      engraving_content: item.engraving_content,
    }));

    // 返回格式：{ data: [...商品陣列] }
    return res(
      ctx.status(200),
      ctx.json({
        data: printItems,
      })
    );
  }
);

// 匯出所有後台訂單 handlers
export const adminOrdersHandlers = [
  getAdminOrdersListHandler,
  getAdminOrderDetailHandler,
  getAdminOrderItemsHandler,
];
